// What the Deliveries board says after Record Delivery (ui-flow F01 UF-06).
//
// The save used to end in a bare "Delivery saved" tick and the board — nothing
// named the truck, so the clerk had to go and find it again to open it or put
// it on a run. The board now shows a banner built from what THIS submit wrote:
// one line per fruit line, each naming the delivery it became (or joined),
// with Open where the delivery is on the server.
//
// The banner model is pure, so the wording and the "may this be linked / put on
// a run" rules are tested without the 3 000-line page.

import type { SupabaseClient } from '@supabase/supabase-js'
import type { DeliveryStatus } from '@/types/database'
import { withReadTimeout } from '@/lib/saveTimeout'
import { countOrThrow } from '@/lib/readState'

/** One fruit line of a gate intake, as the save resolved it. */
export interface SavedIntakeLine {
  /** The delivery this line wrote into. null = queued on this device: the
   *  row is not on the server yet, so nothing may link to it. */
  deliveryId: string | null
  queued: boolean
  variety: string
  bins: number
  /** The status the line's intake path lands in (initialStatusForPath). */
  status: DeliveryStatus
  /** Set when the line was ADDED to a delivery already captured today. */
  joined?: {
    /** This load's number on that delivery (its delivery_additions count,
     *  the same numbering the delivery page shows), null when it could not
     *  be read — the banner then says "another load", never a guessed number. */
    loadNo: number | null
    /** The delivery's bins after this load. */
    totalBins: number
  }
}

export interface SavedIntake {
  grower: string
  /** The truck's delivery note #, '' when none was typed. */
  note: string
  lines: SavedIntakeLine[]
}

export interface SavedBannerLine {
  text: string
  /** Where Open goes — null for a queued line, and for a later line of the
   *  same submit that wrote into a delivery an earlier line already links. */
  openId: string | null
}

export interface SavedBannerModel {
  title: string
  lines: SavedBannerLine[]
  /** Said once when any line is only on this device. */
  queuedNote: string | null
  /** Every line queued: the overlay must not claim the delivery is saved. */
  allQueued: boolean
  /** At least one saved delivery can go on a run now. The Runs page offers
   *  only In Stock / Received deliveries, so a truck still Awaiting drench
   *  gets no Put on a run that would open a list without it. */
  canPutOnRun: boolean
}

export const QUEUED_NOTE = 'Saved on this device, sends when the network is back.'

/** Statuses the Runs page's delivery picker offers (Runs.tsx `available`). */
const RUNNABLE: ReadonlySet<DeliveryStatus> = new Set<DeliveryStatus>(['IN_STOCK', 'RECEIVED'])

const binsText = (n: number) => `${n} ${n === 1 ? 'bin' : 'bins'}`

export function savedIntakeBanner(saved: SavedIntake): SavedBannerModel {
  const grower = saved.grower.trim() || 'Delivery'
  const note = saved.note.trim()
  const allQueued = saved.lines.length > 0 && saved.lines.every(l => l.queued)
  const title = `${allQueued ? 'Saved on this device' : 'Saved'}: ${grower}${note ? ` · note #${note}` : ''}`

  const linked = new Set<string>()
  const lines: SavedBannerLine[] = saved.lines.map(l => {
    const variety = l.variety.trim()
    let text: string
    if (l.joined) {
      const load = l.joined.loadNo ? `as load ${l.joined.loadNo}` : 'as another load'
      text = `${binsText(l.bins)} ${variety} added ${load} to today's delivery, now ${binsText(l.joined.totalBins)}`
    } else {
      text = `${variety} · ${binsText(l.bins)}`
    }
    if (l.queued) text += ' · on this device'
    let openId: string | null = null
    if (!l.queued && l.deliveryId && !linked.has(l.deliveryId)) {
      openId = l.deliveryId
      linked.add(l.deliveryId)
    }
    return { text, openId }
  })

  return {
    title,
    lines,
    queuedNote: saved.lines.some(l => l.queued) ? QUEUED_NOTE : null,
    allQueued,
    canPutOnRun: saved.lines.some(l => !l.queued && !!l.deliveryId && RUNNABLE.has(l.status)),
  }
}

/**
 * The number of the load just added to a delivery: its delivery_additions
 * count once the new row is in (the delivery page numbers loads the same way —
 * the first addition IS load 1). Best effort: the load is already saved, so a
 * failed or slow read returns null and the banner says "another load" rather
 * than failing a save that went through.
 */
// Short: this read sits inside the save, under SubmitOverlay's 60s watchdog,
// and only decides between "load 2" and "another load".
const LOAD_NUMBER_TIMEOUT_MS = 5_000

export async function addedLoadNumber(db: SupabaseClient, deliveryId: string): Promise<number | null> {
  try {
    const n = countOrThrow(await withReadTimeout(db.from('delivery_additions')
      .select('addition_id', { count: 'exact', head: true })
      .eq('delivery_id', deliveryId), 'Deliveries: load number', LOAD_NUMBER_TIMEOUT_MS))
    return n > 0 ? n : null
  } catch {
    return null
  }
}
